<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardLinkSections label="UIC Program">
                <LandingCardLinkSection
                  link-label="Find Form Submissions"
                  description="Find, view, and manage underground injection control form submissions"
                  :to="{ name: routeNames.formSubmission.list }"
                />
                <Authorize v-bind="readFacilityAR" hide-by-default>
                  <LandingCardLinkSection
                    link-label="Find Facilities"
                    description="Find, view, and manage underground injection control facilities"
                    :to="{ name: routeNames.facility.list }"
                  />
                </Authorize>

                <Authorize v-bind="readWellAR" hide-by-default>
                  <LandingCardLinkSection
                    link-label="Find Wells"
                    description="Find, view, and manage underground injection control wells"
                    :to="{ name: routeNames.well.list }"
                  />
                </Authorize>
                <Authorize v-bind="readPermitAR" hide-by-default>
                  <LandingCardLinkSection
                    link-label="Find Permits"
                    description="Find, view, and manage underground injection control permits"
                    :to="{ name: routeNames.permit.list }"
                  />
                </Authorize>
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Analytics" v-if="analyticsAuthorized">
                <Authorize v-bind="readFacilityAR" hide-by-default>
                  <LandingCardLinkSection
                    link-label="Download UIC Facility Data"
                    :to="{ name: routeNames.analytics.facilityDataDownload }"
                  />
                </Authorize>

                <Authorize v-bind="readWellAR" hide-by-default>
                  <LandingCardLinkSection
                    link-label="Download UIC Well Data"
                    :to="{ name: routeNames.analytics.wellDataDownload }"
                  />
                </Authorize>
                <Authorize v-bind="readPermitAR" hide-by-default>
                  <LandingCardLinkSection
                    link-label="Download UIC Permit Data"
                    :to="{ name: routeNames.analytics.permitDataDownload }"
                  />
                </Authorize>
              </LandingCardLinkSections>
            </template>
          </LandingCard>
        </div>
        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <LandingCard title="Form Submissions">
            <template #titleSuffix>
              <Badge :murn="LE.FormSubmissionStatus.Received" :lookup-format="LookupFormat.description" />
            </template>
            <LandingCardSkeleton v-if="areReceivedSubmissionsLoading" />
            <div
              class="bg-neutral-50 p-10 sm:rounded-b-lg"
              v-if="!areReceivedSubmissionsLoading && !receivedSubmissions?.length"
            >
              <NoneFoundFlag label="None Found" />
            </div>
            <LandingCardEntityList
              title="Received &ndash; 5 newest"
              :items="receivedSubmissions"
              :view-all-to="{
                name: routeNames.formSubmission.list,
                query: { statusMurnIn: [LE.FormSubmissionStatus.Received] },
              }"
            >
              <!-- Example prefix. -->
              <!-- <template #titlePrefix>
                <Badge :murn="LE.FormSubmissionStatus.Received" :lookup-format="LookupFormat.description" />
              </template> -->
              <template #default="{ item }">
                <LandingCardEntityListItem>
                  <template #primaryLabel>
                    {{ item.submissionNumber }}
                  </template>
                  <template #secondaryLabel>
                    <LookupDescription :murn="item.formNameMurn"></LookupDescription>
                  </template>
                  <template #additionalInfo>
                    <div
                      class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                      v-if="item.submissionLatestDateTime"
                    >
                      <span class="md:block"
                        >Submitted
                        <span class="text-neutral-500">{{
                          tzDate.shortDateTimeString(item.submissionLatestDateTime)
                        }}</span></span
                      >
                    </div>
                  </template>
                </LandingCardEntityListItem>
              </template>
            </LandingCardEntityList>
          </LandingCard>

          <LandingCard title="Form Submissions">
            <template #titleSuffix>
              <Badge :murn="LE.FormSubmissionStatus.Assigned" :lookup-format="LookupFormat.description" />
            </template>
            <LandingCardSkeleton v-if="areAssignedSubmissionsLoading" />
            <div
              class="bg-neutral-50 p-10 sm:rounded-b-lg"
              v-if="!areAssignedSubmissionsLoading && !assignedSubmissions?.length"
            >
              <NoneFoundFlag label="None Found" />
            </div>
            <LandingCardEntityList
              title="Assigned &ndash; 5 oldest"
              :items="assignedSubmissions"
              :view-all-to="{
                name: routeNames.formSubmission.list,
                query: { statusMurnIn: [LE.FormSubmissionStatus.Assigned] },
              }"
            >
              <!-- <template #titlePrefix>
                <Badge :murn="LE.FormSubmissionStatus.Assigned" :lookup-format="LookupFormat.description" />
              </template> -->
              <template #default="{ item }">
                <LandingCardEntityListItem>
                  <template #primaryLabel>
                    {{ item.submissionNumber }}
                  </template>
                  <template #secondaryLabel>
                    <LookupDescription :murn="item.formNameMurn"></LookupDescription>
                  </template>
                  <template #additionalInfo>
                    <div
                      class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                      v-if="item.assignedToMurn"
                    >
                      <span class="md:block"
                        >Assigned Staff
                        <span class="text-neutral-500">
                          <LookupDescription :murn="item.assignedToMurn"></LookupDescription> </span
                      ></span>
                    </div>
                  </template>
                </LandingCardEntityListItem>
              </template>
            </LandingCardEntityList>
          </LandingCard>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enums";
import { routeNames } from "@/routes";
import { getFormSubmissions } from "@/services/endpoints";
import type { FormSubmission } from "@/views/formSubmission/formSubmission";
import formSubmissionService from "@/views/formSubmission/formSubmissionService";
import { Badge } from "@modernary/mui/components/badge";
import { BaseButton } from "@modernary/mui/components/button";
import { NoneFoundFlag, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import { PublicLandingList, PublicLandingListItem } from "@modernary/mui/components/publicLanding";
import { LookupFormat } from "@modernary/mui/components/read";
import { useToastStore } from "@modernary/mui/components/toasts";
import type { Paging } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { authorizationClient, Authorize, AuthRequest } from "@modernary/mui/utils/authorization";
import { tzDate } from "@modernary/mui/utils/filters";
import { AxiosError } from "axios";
import { defineComponent, ref } from "vue";
import type { LandingCardEntity } from "./landing/landingCard";

export default defineComponent({
  name: "HomeInternal",
  setup() {
    const { userRef } = useUser();
    const toastStore = useToastStore();

    const readWellAR = new AuthRequest();
    readWellAR.permissionName = "ReadWell";

    const readPermitAR = new AuthRequest();
    readPermitAR.permissionName = "ReadPermit";

    const readFacilityAR = new AuthRequest();
    readFacilityAR.permissionName = "ReadFacility";

    const analyticsAuthorized = ref(false);
    const appSettingsStore = useAppSettingsStore();

    authorizationClient.isAuthorizedArray([readPermitAR, readFacilityAR, readWellAR]).then((x) => {
      if (x.find((v) => v == true)) analyticsAuthorized.value = true;
    });

    const areReceivedSubmissionsLoading = ref(1);
    const receivedSubmissions = ref<LandingCardEntity<FormSubmission>[]>();
    getFormSubmissions({
      statusMurnIn: [LE.FormSubmissionStatus.Received],
      orderBy: [{ propertyName: "submissionLatestDateTime", isDescending: true }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          formSubmissionService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<FormSubmission>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.formSubmission.view, params: { id: item.id } };
        });
        receivedSubmissions.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Form Submissions";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areReceivedSubmissionsLoading.value--;
      });

    const areAssignedSubmissionsLoading = ref(1);
    const assignedSubmissions = ref<LandingCardEntity<FormSubmission>[]>();
    getFormSubmissions({
      statusMurnIn: [LE.FormSubmissionStatus.Assigned],
      orderBy: [{ propertyName: "submissionLatestDateTime", isDescending: false }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          formSubmissionService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<FormSubmission>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.formSubmission.view, params: { id: item.id } };
        });
        assignedSubmissions.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Form Submissions";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areAssignedSubmissionsLoading.value--;
      });

    return {
      userRef,
      routeNames,
      BaseButton,
      appSettingsStore,
      LookupFormat,
      LE,
      areReceivedSubmissionsLoading,
      receivedSubmissions,
      tzDate,
      areAssignedSubmissionsLoading,
      assignedSubmissions,
      readWellAR,
      readPermitAR,
      readFacilityAR,
      analyticsAuthorized,
    };
  },
  components: {
    Badge,
    LandingBackground,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LookupDescription,
    NoneFoundFlag,
    PublicLandingList,
    PublicLandingListItem,
    Authorize,
  },
});
</script>
